<template>
  <div id="home">
    <div class="upcoming">
      <div class="container">
        <div class="overflow-auto">
          <div class="d-flex justify-content-start align-items-stretch">
            <my-activities-summary :key="activitiesUpdated" started>
              In progress
            </my-activities-summary>
            <unfiled-client-incident-reports />
            <my-activities-summary :key="activitiesUpdated" today>
              To start
            </my-activities-summary>
            <my-activities-summary :key="activitiesUpdated" retry>
              To retry
            </my-activities-summary>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <h2 class="pt-1 show-if-activities">Your Rotas</h2>
      <div id="rota-select">
        <div class="d-flex flex-row mb-1">
          <RotaSelect
            v-if="rotas.length > 0 || loadingRotas"
            v-model="selectedRota"
            :options="rotas"
            :loading="loadingRotas || isLoadingComponent"
            class="flex-grow-1 flex-min-width-0"
          />
          <div class="btn-group ms-1 text-nowrap">
            <router-link
              :class="`btn btn-outline-secondary btn-sm d-print-none ${
                selectedRota === null ? 'disabled' : ''
              }`"
              :to="
                selectedRota !== null
                  ? {
                      name: 'rota/RotaView',
                      params: { rotaId: selectedRota.id },
                    }
                  : {}
              "
            >
              <font-awesome-icon icon="calendar-alt" /> View rota
            </router-link>
          </div>
        </div>
        <template v-if="!loadingRotas">
          <div v-if="rotas.length === 0 || !selectedRota">
            <b-alert :model-value="true" variant="warning">
              <font-awesome-icon icon="exclamation-circle" fixed-width />
              <template v-if="rotas.length === 0">
                No rotas are available for today's date.
              </template>
              <template v-else>
                You are not assigned to any current published rotas.
              </template>
              Please check back later or
              <router-link :to="{ name: 'rota/RotaList' }"
                >view all rotas</router-link
              >.
            </b-alert>
          </div>
          <div class="mb-2">
            <div class="d-flex justify-content-between align-items-end">
              <div v-if="$can('create', 'rota.rota-activities')">
                <button
                  autocomplete="off"
                  class="btn btn-primary btn-sm"
                  type="button"
                  :disabled="isLoadingComponent"
                  @click="addActivity"
                >
                  <font-awesome-icon icon="plus" />
                  New activity
                </button>
              </div>
              <div v-if="selectedRota !== null" class="text-end">
                <div
                  v-if="!isLoadingComponent"
                  class="btn-group me-2 view-coverable-shifts"
                  role="group"
                >
                  <input
                    id="view-coverable-shifts"
                    v-model="showCoverableShifts"
                    type="checkbox"
                    class="me-1"
                    autocomplete="off"
                    :disabled="isLoadingComponent"
                  />
                  <label for="view-coverable-shifts">
                    Show shift cover
                    <font-awesome-icon icon="people-arrows" />
                  </label>
                </div>
                <div
                  v-if="!isLoadingComponent"
                  class="btn-group"
                  role="group"
                  aria-label="Switch Rota Views"
                >
                  <input
                    id="view-agenda"
                    v-model="viewMode"
                    value="agenda"
                    type="radio"
                    class="btn-check btn-sm"
                    name="btnradio"
                    autocomplete="off"
                    :disabled="isLoadingComponent"
                  />
                  <label
                    class="btn btn-sm btn-outline-primary"
                    for="view-agenda"
                  >
                    <font-awesome-icon
                      v-show="isLoadingComponent && viewMode === 'agenda'"
                      class="me-2"
                      icon="spinner"
                      spin
                    />
                    <font-awesome-icon icon="calendar-week" />
                    Agenda
                  </label>
                  <input
                    id="view-month"
                    v-model="viewMode"
                    value="month"
                    type="radio"
                    class="btn-check"
                    name="btnradio"
                    autocomplete="off"
                    :disabled="isLoadingComponent"
                  />
                  <label
                    class="btn btn-sm btn-outline-primary"
                    for="view-month"
                  >
                    <font-awesome-icon
                      v-show="isLoadingComponent && viewMode === 'month'"
                      class="me-2"
                      icon="spinner"
                      spin
                    />
                    <font-awesome-icon icon="calendar-alt" />
                    Month
                  </label>
                </div>
                <div
                  v-else
                  class="btn-group btn-group-sm placeholder bg-light"
                  role="group"
                  aria-label="Switch Rota Views"
                >
                  <button type="button" class="btn disabled placeholder">
                    <font-awesome-icon icon="calendar-week" />
                    Agenda
                  </button>
                  <button type="button" class="btn disabled placeholder">
                    <font-awesome-icon icon="calendar-alt" />
                    Month
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div v-if="selectedRota !== null">
        <div v-if="isLoadingComponent" class="text-center">
          <b-spinner label="Loading..." />
        </div>
        <rota-user-agenda-view
          v-if="viewMode === 'agenda'"
          :rota="selectedRota"
          :activities="activitiesForAgendaView"
          :available-shifts="availableShifts"
          :options="{ showCoverableShifts }"
          @rota-shift-self-assign="onSelfAssignBtn"
          @vue:mounted="isLoadingComponent = false"
        />
        <rota-user-month-view
          v-if="viewMode === 'month'"
          :rotas="rotas"
          :activities="activities"
          :options="{ showCoverableShifts }"
          class="mt-2"
          @vue:mounted="isLoadingComponent = false"
        />
      </div>
      <template v-if="loadingRotas">
        <div
          v-for="n in ['today', 1, 2, 3, 4, 5, 6, 7]"
          :key="n"
          class="card mb-4 mt-2 loadingRotas"
          aria-hidden="true"
          :class="n"
        >
          <div class="card-body">
            <h5 class="card-title placeholder-glow">
              <span class="placeholder col-12"></span>
            </h5>
            <div class="row">
              <div class="col-8">
                <p class="card-text placeholder-glow">
                  <span class="placeholder col-12"></span>
                </p>
              </div>
              <div class="col-4">
                <p class="card-text placeholder-glow">
                  <span class="placeholder col-12"></span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  computed,
  ref,
  watch,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  toRaw,
} from "vue";
import { mapGetters, useStore } from "vuex";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import get from "lodash/get";
import { Hub } from "aws-amplify/utils";
import { useAbility } from "@casl/vue";
import { endOfDay, startOfDay, subDays, addDays } from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import { useRotaStore } from "@/store/rotas";
import { todayIsInRota } from "@/lib/date-functions";
import { rotaHasActivities } from "@/lib/rota/activity-functions";
import {
  userIsOnThisRota,
  agencyIsOnThisRota,
  userHasVolunteeredOnThisRota,
} from "@/lib/rota/rotauser-functions";
import RotaSelect from "@/components/rota/RotaSelect.vue";
import RotaUserAgendaView from "@/components/rota/agenda/RotaUserAgendaView.vue";
import RotaUserMonthView from "@/components/rota/agenda/RotaUserMonthView.vue";
import useRotaAccessControls from "@/components/rota/functions/useRotaAccessControls";
import usePopupActivity from "@/components/activity/usePopupActivity";
import MyActivitiesSummary from "@/components/activity/MyActivitiesSummary.vue";
import UnfiledClientIncidentReports from "@/components/activity/UnfiledClientIncidentReports.vue";
import useShiftCoverVolunteerSequence from "@/components/form/sequences/useShiftCoverVolunteerSequence";
import useFormSequenceModal from "@/components/form/useFormSequenceModal";

export default {
  name: "HomeView",
  components: {
    MyActivitiesSummary,
    UnfiledClientIncidentReports,
    RotaUserAgendaView,
    RotaUserMonthView,
    RotaSelect,
  },
  setup() {
    const { can } = useAbility();
    const { init, authUserCanAccessRota } = useRotaAccessControls();

    const allRotas = ref([]); // all rotas returned from api

    const selectedRota = ref(null);
    const store = useStore();
    const authStore = useAuthStore();
    const rotaStore = useRotaStore();
    const loadingRotas = ref(true);
    const activities = ref(false);
    const activitiesUpdated = ref(0);
    const activitiesForSelectedRota = ref(false);
    const availableShifts = ref(false);

    const viewMode = ref("agenda");
    const isLoadingComponent = ref(false);
    const showCoverableShifts = ref(true);
    const allActivitiesLoaded = ref(false);

    let hubActivityListener;
    let hubShiftListener;

    onBeforeMount(() => {
      hubActivityListener = Hub.listen("activity", ({ payload }) => {
        let index = -1;
        switch (payload.event) {
          case "updated":
            index = findIndex(activities.value, { id: payload.data.id });
            if (index !== -1) {
              activities.value.splice(index, 1, payload.data);
            }
            activitiesUpdated.value += 1;
            break;
          case "deleted":
            index = findIndex(activities.value, { id: payload.data.id });
            if (index !== -1) {
              activities.value.splice(index, 1);
            }
            activitiesUpdated.value += 1;
            Hub.dispatch("popup-activity", {
              event: "hide",
            });
            break;
          default:
        }
      });
      hubShiftListener = Hub.listen("shift", ({ payload }) => {
        if (payload.event === "updated") {
          // find the shift in any of the current rotas
          const rota = find(rotas.value, (r) =>
            r.shifts.some((s) => s.id === payload.data.id),
          );
          if (rota) {
            const shiftIndex = findIndex(rota.shifts, { id: payload.data.id });
            if (shiftIndex !== -1) {
              rota.shifts.splice(shiftIndex, 1, payload.data);
            }
          }
          // find the shift in the available shifts
          const availableRota = find(availableShifts.value, (r) =>
            r.shifts.some((s) => s.id === payload.data.id),
          );
          if (availableRota) {
            const shiftIndex = findIndex(availableRota.shifts, {
              id: payload.data.id,
            });
            if (shiftIndex !== -1) {
              availableRota.shifts.splice(shiftIndex, 1, payload.data);
            }
          }
        }
      });
    });

    onBeforeUnmount(() => {
      hubActivityListener();
      hubShiftListener();
    });

    onMounted(() => {
      init().then(() => fetchData());
    });

    const mergeActivities = (arrActivities) => {
      if (activities.value === false) {
        activities.value = arrActivities;
      } else {
        for (const activity of arrActivities) {
          const existingIndex = findIndex(activities.value, {
            id: activity.id,
          });
          if (existingIndex === -1) {
            activities.value.push(activity);
          }
        }
      }
    };

    if (!authStore.isAgencyUser) {
      // availableshifts API is already filtered based on user location access rights
      rotaStore.getAvailableShifts().then((data) => {
        if (data !== false) {
          availableShifts.value = data.rotas;
          if (activities.value === false) {
            activities.value = data.activities;
          } else {
            mergeActivities(data.activities);
          }
        }
      });
    }

    // rotas we should see
    const rotas = computed(() => {
      if (can("update", "rota.rotas")) {
        return allRotas.value;
      }
      return allRotas.value.filter((r) => authUserCanAccessRota(r));
    });

    const fetchData = async () => {
      const rotaData = await store.dispatch("rotas/getCurrentRotas");
      if (
        rotaData === false ||
        (Array.isArray(rotaData) && rotaData.length == 0)
      ) {
        loadingRotas.value = false;
        return;
      }
      allRotas.value = rotaData;
      loadingRotas.value = false;

      // fetch activities across all rotas
      // - this is a fast way to get the agenda view rendering, since we don't
      // need ALL the activities from a rota, just the ones we're assigned to
      let minStartDate = null;
      let maxEndDate = null;
      rotaData.forEach((rota) => {
        if (minStartDate === null || rota.startDate < minStartDate) {
          minStartDate = rota.startDate;
        }
        if (maxEndDate === null || rota.endDate > maxEndDate) {
          maxEndDate = rota.endDate;
        }
      });
      store
        .dispatch("rotaactivities/getActivitiesForUser", {
          startDate: startOfDay(subDays(minStartDate, 1)),
          endDate: endOfDay(addDays(maxEndDate, 1)),
          includeUnassigned: "true",
        })
        .then((data) => {
          allActivitiesLoaded.value = true;
          if (data !== false) {
            if (activities.value === false) {
              activities.value = data;
            } else {
              mergeActivities(data);
            }
          }
        });
      preSelectRota(rotaData);
    };

    const preSelectRota = (rotaData) => {
      // pre-select rota if i'm on it
      rotaData.forEach((rota) => {
        if (
          todayIsInRota(rota.startDate) &&
          userIsOnThisRota(authStore.user.id, rota)
        ) {
          selectedRota.value = rota;
        }
      });
      if (selectedRota.value == null) {
        // pre-select rota if we've volunteered on it
        rotaData.forEach((rota) => {
          if (
            todayIsInRota(rota.startDate) &&
            userHasVolunteeredOnThisRota(authStore.user.id, rota)
          ) {
            selectedRota.value = rota;
          }
        });
        if (authStore.isAgencyUser) {
          // pre-select rota if the agency is on it
          rotaData.forEach((rota) => {
            if (
              todayIsInRota(rota.startDate) &&
              agencyIsOnThisRota(authStore.user.agencyId, rota)
            ) {
              selectedRota.value = rota;
            }
          });
        }
      }
    };

    const { showAddActivityPopup, newActivity } = usePopupActivity();

    watch(newActivity, (newItem) => {
      if (newItem) {
        if (activities.value === false) {
          activities.value = [];
        }
        activities.value.push(newItem);
        activitiesUpdated.value += 1;
      }
    });

    const activitiesForAgendaView = computed(() => {
      if (activities.value === false) {
        return false;
      }
      if (allActivitiesLoaded.value === false) {
        return false;
      }
      if (selectedRota.value === null) {
        return activities.value;
      }
      return activities.value.filter(
        (activity) => activity.locationId == selectedRota.value.location,
      );
    });

    const onSelfAssignBtn = (shift) => {
      const { showFormSequenceModal, hideFormSequenceModal } =
        useFormSequenceModal();
      showFormSequenceModal(
        "Volunteer for shift cover",
        "people-arrows",
        useShiftCoverVolunteerSequence,
        {
          shift: toRaw(shift),
        },
        async () => {
          let matchingRota;
          availableShifts.value.forEach((rota) => {
            if ((rota.shifts ?? []).find((s) => s.id === shift.id)) {
              matchingRota = rota;
            }
          });

          if (!matchingRota) {
            Hub.dispatch("toast", {
              event: "error",
              message: `Could not find matching rota for shift cover. Please contact an administrator for assistance.`,
              data: {
                timeout: 10000,
              },
            });
            console.error(
              "Could not find matching rota for shift cover.",
              shift.id,
            );
            return;
          }

          const newShift = await rotaStore.submitCoverVolunteer(
            matchingRota.id,
            shift.id,
          );
          Hub.dispatch("shift", {
            event: "updated",
            data: newShift,
          });
          Hub.dispatch("toast", {
            event: "info",
            message: `Volunteered for shift cover. You will be notified if your cover is accepted.`,
            data: {
              timeout: 10000,
            },
          });
          hideFormSequenceModal();
          return true;
        },
      );
      return;
    };

    return {
      find,
      rotas,
      activities,
      activitiesForAgendaView,
      activitiesForSelectedRota,
      activitiesUpdated,
      availableShifts,
      loadingRotas,
      selectedRota,
      rotaHasActivities,
      todayIsInRota,
      showAddActivityPopup,

      viewMode,
      isLoadingComponent,
      onSelfAssignBtn,
      showCoverableShifts,
    };
  },
  computed: {
    ...mapGetters({
      locations: "locations",
    }),
  },
  watch: {
    viewMode: {
      handler() {
        this.displayLoadingIndicator();
      },
    },
    selectedRota: {
      handler(newRota, oldRota) {
        if (newRota == null || newRota === oldRota) {
          return;
        }
        if (this.viewMode === "week") {
          this.displayLoadingIndicator();
        }
        if (newRota) {
          this.activitiesForSelectedRota = false;
          if (rotaHasActivities(newRota)) {
            this.$store
              .dispatch("rotaactivities/getActivitiesForRota", newRota.id)
              .then((data) => {
                if (data !== false) {
                  this.activitiesForSelectedRota = data;
                }
              });
          } else {
            this.activitiesForSelectedRota = [];
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Display a loading indicator.
     *
     * Components are hidden via `shouldRenderComponent` and then shown again
     * after the next tick. This is to prevent the DOM being locked before the
     * loading indicator is shown.
     */
    displayLoadingIndicator() {
      this.isLoadingComponent = true;
    },
    addActivity() {
      const defaultLocation = get(this.selectedRota, "location", "gh");
      this.showAddActivityPopup({
        rotaActivity: {
          locationId: defaultLocation,
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.tabs.agenda :deep(.nav-tabs) {
  border-bottom: none;
}

.show-if-activities:only-child {
  // hide heading if there are no activities
  display: none;
}
</style>

<style lang="scss">
#home .activities .activity.finished {
  opacity: 0.8;
  border-width: 0px;
}
#home .activities .activity.started {
  border-color: #07981f;
}
#home .upcoming:has(h2) {
  background-color: #f0f0f0;
  margin-top: -14px;
  padding-top: 14px;
  padding-bottom: 0.5em;
  margin-bottom: 0.5em;
  border-bottom: 2px dashed #ccc;
  h2 {
    font-size: 120%;
    font-weight: bold;
    white-space: nowrap;
  }
  .activities-container {
    display: flex;
    align-items: stretch;
    .card {
      min-width: 20em;
    }
  }
  .unfiled-incidents {
    .activity-container {
      min-width: 22em;
      .card {
        max-height: 16em;
        overflow: scroll;
      }
      .alert {
        margin-bottom: 0px;
      }
    }
  }
  .activity-container {
    margin-right: 0.5em;
  }
  .d-flex {
    margin-bottom: 0.25em;
    > div {
      display: flex;
      flex-direction: column;
      margin-bottom: 0.25em;
      padding-left: 0.5em;
      border-left: 2px dotted #ccc;
      &:first-child {
        border-left: none;
        padding-left: 0px;
      }
    }
    .activities-container {
      flex: 1;
    }
  }
  .my-activities {
    .card {
      cursor: pointer;
      &.old:not(.finished) {
        border-color: var(--activity-status-alert-border-color);
        border-width: 2px;
        &:after {
          content: "Needs Finishing";
          position: absolute;
          top: 0;
          right: 0;
          background-color: var(--activity-status-alert-border-color);
          animation: var(--animate-pulse);
          color: #fff !important;
          border-bottom-left-radius: 0.5em;
          color: var(--activity-status-alert-text-color);
          padding: 0.25em 0.5em;
          font-size: 0.75em;
          font-weight: bold;
        }
      }
    }
  }
  .card {
    height: 100%;
    --bs-card-spacer-x: 0.5em;
    --bs-card-spacer-y: 0.5em;
    &:hover {
      box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.1);
    }
    a {
      text-decoration: none; // disable link style because the whole card is clickable
      color: #333 !important;
    }
  }
}
#home .view-coverable-shifts {
  color: var(--cover-request-color);
}
#home {
  .loadingRotas {
    &.today {
      border-left: 4px solid #0a58ca;
      background: $blue-100;
    }
  }
}
</style>
